import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFeatureFlagEnabled, usePostHog } from "posthog-js/react";

import { useCase, useSetDcOrderingPreference } from "hooks/useArrangementAPI";
import { ApiErrorCode, apiErrorCode } from "utils/errors";

import { useArrangement } from "./ArrangementContext";
import {
  DC_HINT,
  DC_ORDERING,
  QUESTION_ID,
  dcAnswerCorrections,
  dcContinueHint,
  isDcOrderingLocked,
  isShippingAddressComplete,
  resolveDcOrdering,
} from "./helpers";
import ArrangementNav from "./ArrangementNav";
import BackToReviewButton from "./BackToReviewButton";
import ServiceQuestion from "./ServiceQuestion";
import { useLovedOneFirstName } from "./useLovedOneFirstName";
import { getDcOrderingTemplate } from "./dcOrderingTemplates";
import DeliveryAddressForm, {
  DeliveryAddressSummary,
} from "./DeliveryAddressForm";
import { getServicesScreenQuestions, serviceScreenName } from "./screenModel";
import { useScreenTracking } from "./useScreenTracking";
import type {
  CaseDeathCertificateState,
  ServiceAnswer,
  ServiceQuestion as ServiceQuestionType,
} from "./types";

function getContinueLabel(
  currentQuestion: { type: string } | null | undefined,
): string {
  // Affirmations keep their specific verb; every other forward action is a
  // plain "Continue" (verbose "Continue to X" labels are hard to fit on mobile).
  if (currentQuestion?.type === "info") return "I acknowledge";
  return "Continue";
}

function shouldShowDeliveryAddressForm(
  questionId: string,
  serviceAnswers: ServiceAnswer[],
): boolean {
  const deliveryAnswer = serviceAnswers.find(
    (answer) => answer.questionId === QUESTION_ID.DELIVERY,
  );

  return (
    questionId === QUESTION_ID.DELIVERY &&
    !!deliveryAnswer?.selectedOptionId &&
    deliveryAnswer.selectedOptionId !== "pickup"
  );
}

const ignoreServiceAnswer = (_answer: ServiceAnswer) => undefined;

export const DC_SELF_SERVE_FLAG = "at-need-dc-self-serve";

export default function ServicesStep() {
  const navigate = useNavigate();
  const { caseId } = useParams<{ caseId: string }>();
  const {
    state,
    setServiceAnswer,
    setServiceAnswersAndFlush,
    packageInclusions,
    config,
    isReadOnly,
  } = useArrangement();

  const posthog = usePostHog();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [addressFormValid, setAddressFormValid] = useState(true);

  // Ships dark. The credit is displayed here but is not applied to the quote
  // until fee syncing credits it server-side, so this stays off in production
  // until that lands.
  const dcSelfServeEnabled = !!useFeatureFlagEnabled(DC_SELF_SERVE_FLAG);
  const lovedOneName = useLovedOneFirstName();

  // The death-certificate question needs the case itself, not just the draft:
  // the case records who was decided to order and, once they have been ordered,
  // how many. The outer flow's read skips @after.com users and mounts through
  // errors, so this step reads for itself rather than borrowing that one.
  const {
    data: caseData,
    error: caseError,
    mutate: revalidateCase,
  } = useCase(caseId);
  const { trigger: writeDcOrderingPreference } =
    useSetDcOrderingPreference(caseId);

  const caseDc = caseData as CaseDeathCertificateState | undefined;
  const isCaseDcKnown = !!caseDc;
  const isDcOrderPlaced = isDcOrderingLocked(caseDc);

  // What was ordered, normalized the way the reconcile and the confirmation
  // gate normalize it: a placed order the case recorded no number for is an
  // order of none. The quantity shown then comes from here rather than from the
  // draft or the plan's included count, neither of which is what was bought.
  const orderedDcQuantity = isDcOrderPlaced
    ? (caseDc?.number_of_death_certificates ?? 0)
    : undefined;

  // Cleared by any state change that could make the next attempt succeed.
  const [dcWriteBlock, setDcWriteBlock] = useState<
    "save_failed" | "locked" | null
  >(null);
  const [isSavingDcOrdering, setIsSavingDcOrdering] = useState(false);
  // Synchronous, unlike the state above: it has to be set before the first
  // await, and a second click arrives long before React re-renders.
  const dcContinueInFlight = useRef(false);

  // A full server-side template wins; otherwise resolve the county's key
  // against the copy that ships with the client.
  const dcOrderingTemplate =
    config?.dc_ordering_template ??
    getDcOrderingTemplate(config?.dc_ordering_template_key);

  // Enrich the Services-screen questions with package inclusion data.
  const questions: ServiceQuestionType[] = useMemo(
    () =>
      getServicesScreenQuestions(config).map((q) => {
        if (
          q.id === QUESTION_ID.DEATH_CERTS &&
          q.stepperConfig &&
          packageInclusions
        ) {
          return {
            ...q,
            stepperConfig: {
              ...q.stepperConfig,
              includedQuantity: packageInclusions.includedDeathCerts,
            },
          };
        }
        return q;
      }),
    [packageInclusions, config],
  );

  const totalPages = questions.length;
  const currentQuestion = questions[currentIndex];
  const currentAnswer = currentQuestion
    ? state.serviceAnswers.find((a) => a.questionId === currentQuestion.id)
    : undefined;

  const dcOrderingAnswer = state.serviceAnswers.find(
    (a) => a.questionId === QUESTION_ID.DC_ORDERING,
  );

  const screenDescriptor = currentQuestion
    ? {
        screen_name: serviceScreenName(currentQuestion.id),
        step: "services" as const,
        question_id: currentQuestion.id,
      }
    : null;
  const { markCompleted } = useScreenTracking(screenDescriptor);

  // Nothing the package includes outranks an order that has already been
  // placed: on those cases the count comes from the case instead, and writing
  // the package's number first would leave the draft disagreeing with a paid
  // order until the reconcile overwrote it. Waiting for the read to settle
  // costs a beat. Where the self-serve choice is switched off the family is
  // never shown that the read failed, so falling back to the package default
  // keeps a case whose read is broken from silently ordering no certificates at
  // all; where it is on, the question is blocked and says so until the read
  // succeeds, and a count written behind that block would only be one more
  // thing to reconcile against the case.
  const canDefaultDeathCertQuantity =
    isCaseDcKnown || (!!caseError && !dcSelfServeEnabled);

  // Auto-default delivery and death cert answers based on package inclusions
  useEffect(() => {
    if (!packageInclusions) return;

    const hasDeliveryAnswer = state.serviceAnswers.some(
      (a) => a.questionId === QUESTION_ID.DELIVERY,
    );
    if (!hasDeliveryAnswer && packageInclusions.includedDeliveryOptionId) {
      setServiceAnswer({
        questionId: QUESTION_ID.DELIVERY,
        selectedOptionId: packageInclusions.includedDeliveryOptionId,
      });
    }

    if (!canDefaultDeathCertQuantity) return;
    if (isDcOrderPlaced) return;

    const hasDeathCertAnswer = state.serviceAnswers.some(
      (a) => a.questionId === QUESTION_ID.DEATH_CERTS,
    );
    if (!hasDeathCertAnswer && packageInclusions.includedDeathCerts > 0) {
      setServiceAnswer({
        questionId: QUESTION_ID.DEATH_CERTS,
        quantity: packageInclusions.includedDeathCerts,
      });
    }
  }, [packageInclusions, canDefaultDeathCertQuantity, isDcOrderPlaced]); // eslint-disable-line react-hooks/exhaustive-deps

  // Bring a draft the family can't repair themselves back in line with the
  // case: the confirmation gate compares the two, and with the question
  // switched off or already answered by an order, nothing else in the flow can
  // produce the answer the gate demands. Converges by construction —
  // `dcAnswerCorrections` returns nothing once the two agree. The one draft it
  // deliberately leaves alone is an orphan answer on a case with no recorded
  // preference, an accepted manual-repair state. Resolves with the corrected
  // answers so a caller mid-recovery can reconcile against them.
  const applyDcCorrections = useCallback(
    async (
      caseDcState: CaseDeathCertificateState,
      serviceAnswers: ServiceAnswer[],
    ): Promise<ServiceAnswer[]> => {
      const corrections = dcAnswerCorrections(
        serviceAnswers,
        caseDcState,
        dcSelfServeEnabled && !isDcOrderingLocked(caseDcState),
      );
      if (corrections.length === 0) return serviceAnswers;
      return setServiceAnswersAndFlush(corrections);
    },
    [dcSelfServeEnabled, setServiceAnswersAndFlush],
  );

  useEffect(() => {
    if (isReadOnly || !caseDc) return;
    applyDcCorrections(caseDc, state.serviceAnswers).catch(() => {
      // The corrections are in state either way, so the ordinary autosave
      // carries them; nothing here is worth interrupting the family for.
    });
  }, [caseDc, state.serviceAnswers, isReadOnly, applyDcCorrections]);

  const getInclusionProps = (questionId: string) => {
    if (questionId === QUESTION_ID.DELIVERY && packageInclusions) {
      return {
        packageInclusions,
      };
    }
    if (questionId === QUESTION_ID.REFRIGERATION && packageInclusions) {
      return {
        packageInclusions,
        includedRefrigerationDays: packageInclusions.includedRefrigerationDays,
      };
    }
    return { packageInclusions };
  };

  const inclusionProps = currentQuestion
    ? getInclusionProps(currentQuestion.id)
    : {};

  const isFirst = currentIndex === 0;
  const isLast = currentIndex === totalPages - 1;

  // Require delivery address when delivery option is not pickup
  const needsDeliveryAddress =
    currentQuestion?.id === QUESTION_ID.DELIVERY &&
    currentAnswer?.selectedOptionId &&
    currentAnswer.selectedOptionId !== "pickup";

  const isDeathCertQuestion = currentQuestion?.id === QUESTION_ID.DEATH_CERTS;

  // One value for the rendering, the guard below and the write.
  const effectiveDcOrdering = resolveDcOrdering({
    caseDc,
    draftAnswer: dcOrderingAnswer?.selectedOptionId,
    packageInclusions,
  });

  const packageTitle = config?.packages.find(
    (p) => p.variant_id === state.selectedPackageId,
  )?.title;

  // Each time the family switches who orders. The final answer goes on the
  // screen's completion event instead.
  const setServiceAnswerTracked = (answer: ServiceAnswer) => {
    if (
      answer.questionId === QUESTION_ID.DC_ORDERING &&
      answer.selectedOptionId !== effectiveDcOrdering
    ) {
      posthog?.capture("arrangement_dc_ordering_selected", {
        case_id: caseId,
        dc_ordering: answer.selectedOptionId,
        package_title: packageTitle,
      });
    }
    setServiceAnswer(answer);
  };

  // "Not read yet" and "read, nothing recorded" are different answers, and only
  // the second is safe to write against: an unlocked case accepts an overwrite,
  // so sending a package default while the case is unknown can replace a
  // preference the family did choose with one they were never shown — and the
  // confirmation gate, seeing draft and case agree, would never catch it.
  const isDcOrderingBlocked =
    dcSelfServeEnabled && isDeathCertQuestion && !isCaseDcKnown;

  // Ordered, still unread, or mid-write: three different reasons the question
  // isn't the family's to answer at this moment.
  const isDcQuestionReadOnly =
    isDcOrderPlaced || isDcOrderingBlocked || isSavingDcOrdering;

  // Asking us to order zero certificates isn't an order. It only comes up on
  // packages that include none (Base), where the stepper starts at zero; the
  // way through is to raise it or to choose self-serve. Never applies once the
  // order has been placed, where the count is the case's and not the family's.
  const isEmptyDeathCertOrder =
    dcSelfServeEnabled &&
    isDeathCertQuestion &&
    !isDcOrderPlaced &&
    effectiveDcOrdering === DC_ORDERING.AFTER &&
    (currentAnswer?.quantity ?? packageInclusions?.includedDeathCerts ?? 0) ===
      0;

  // Packages that include certificates preselect nothing, so the family has to
  // pick one before moving on.
  const isDcOrderingUnanswered =
    dcSelfServeEnabled &&
    isDeathCertQuestion &&
    isCaseDcKnown &&
    !isDcOrderPlaced &&
    !effectiveDcOrdering;

  const canContinue =
    (!needsDeliveryAddress ||
      (isShippingAddressComplete(state.fulfillment.shippingAddress) &&
        addressFormValid)) &&
    !isDcOrderingUnanswered &&
    !isEmptyDeathCertOrder &&
    !isDcOrderingBlocked &&
    !isSavingDcOrdering;

  const isRushYes =
    currentQuestion?.id === QUESTION_ID.RUSH_CREMATION &&
    currentAnswer?.selectedOptionId === "yes";
  const dcHint = !isDeathCertQuestion
    ? undefined
    : dcContinueHint({
        writeBlock: dcWriteBlock,
        isDcOrderPlaced,
        dcSelfServeEnabled,
        isCaseReadBlocked: isDcOrderingBlocked,
        hasCaseReadError: !!caseError,
      });
  const continueHint = dcHint ? (
    <span className="text-sm text-soil/70">{dcHint}</span>
  ) : isDcOrderingUnanswered ? (
    <span className="text-sm text-soil/70">{DC_HINT.UNANSWERED}</span>
  ) : isEmptyDeathCertOrder ? (
    <span className="text-sm text-soil/70">{DC_HINT.EMPTY_ORDER}</span>
  ) : isRushYes ? (
    <span className="text-sm text-soil/70">
      Rush speeds up the cremation only — permit timing is set by the county.
    </span>
  ) : undefined;

  const handleBack = () => {
    if (isFirst) {
      navigate("../packages", { state: { section: "add-ons" } });
    } else {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const advance = (completedProps?: Record<string, unknown>) => {
    markCompleted(completedProps);
    if (isLast) {
      navigate("../confirmation");
    } else {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  // Care ordered the certificates while this step was open. The cached case
  // predates the lock by definition, so re-read it and restore both halves of
  // the question from what came back — a stepper value left over from the draft
  // would otherwise keep quoting copies that are already ordered and paid for.
  const restoreOrderedDcAnswers = async (serviceAnswers: ServiceAnswer[]) => {
    const fresh = (await revalidateCase()) as
      CaseDeathCertificateState | undefined;
    if (!fresh) throw new Error("Case death certificate state unavailable");

    await applyDcCorrections(fresh, serviceAnswers);
  };

  const handleContinue = async () => {
    if (dcContinueInFlight.current) return;

    // Every other question is carried by the ordinary autosave. So is this one
    // once the certificates have been ordered: the case is already decided, the
    // reconcile has matched the draft to it, and there is nothing left to send.
    // With nothing chosen there is nothing to record; canContinue keeps the
    // family here until they pick.
    if (
      !isDeathCertQuestion ||
      !dcSelfServeEnabled ||
      isDcOrderPlaced ||
      !effectiveDcOrdering
    ) {
      advance();
      return;
    }

    dcContinueInFlight.current = true;
    setIsSavingDcOrdering(true);
    setDcWriteBlock(null);
    // The answers as they stand after this handler's own write. React commits
    // them a render later, so the recovery below can't read them off state.
    let answered = state.serviceAnswers;
    try {
      // Recorded only as the family moves past the screen. Writing the shown
      // default on mount would record a preference for someone who never
      // engaged with the question, and `family_orders` suppresses Care's
      // certificate task — an absent answer has to keep meaning "never asked".
      //
      // The draft and the case are written in the same breath: a swallowed
      // autosave failure would otherwise leave the case holding a preference
      // the draft doesn't carry, and the confirmation gate would refuse a state
      // the family never caused.
      answered = await setServiceAnswersAndFlush([
        {
          questionId: QUESTION_ID.DC_ORDERING,
          selectedOptionId: effectiveDcOrdering,
        },
      ]);

      await writeDcOrderingPreference({
        dc_ordering_preference: effectiveDcOrdering,
      });
      advance({
        dc_ordering: effectiveDcOrdering,
        package_title: packageTitle,
      });
    } catch (error) {
      const isConflict = (error as { statusCode?: number })?.statusCode === 409;
      const errorCode = apiErrorCode(error);

      // Ordered while this step was open: restore the case's own values and
      // turn the question read-only rather than trapping the family on a
      // choice that no longer exists.
      if (
        isConflict &&
        errorCode === ApiErrorCode.Arrangement.DC_ORDERING_PREFERENCE_LOCKED
      ) {
        try {
          await restoreOrderedDcAnswers(answered);
          setDcWriteBlock("locked");
        } catch {
          setDcWriteBlock("save_failed");
        }
        return;
      }

      // Advancing on anything else would split the truth: the quote and the
      // draft would read self-serve while the case still says nobody was asked.
      setDcWriteBlock("save_failed");
    } finally {
      dcContinueInFlight.current = false;
      setIsSavingDcOrdering(false);
    }
  };

  return (
    <div className="pb-24">
      {/*
        No step heading here: "Services" only repeated the progress-bar label,
        and its caption ("a few remaining questions") competed with the actual
        question. The question below is the page's h1 instead.
      */}
      <div className="mb-8 text-center">
        {/*
          The read-only view stacks every question on one page, so it needs a
          page heading of its own; the questions below it drop to h2. On the
          wizard path the single question is the h1 and no step heading is
          rendered.
        */}
        {isReadOnly && (
          <h1 className="mb-6 font-serif text-4xl text-soil">Services</h1>
        )}

        {/* Substep progress */}
        <div className="mx-auto flex items-center justify-center gap-2">
          {Array.from({ length: totalPages }, (_, idx) => (
            <div
              key={idx}
              className={`h-1.5 w-10 rounded-full transition-colors ${
                idx <= currentIndex ? "bg-sage" : "bg-oat"
              }`}
            />
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-2xl">
        {isReadOnly ? (
          <div className="pointer-events-none space-y-10">
            {questions.map((q) => (
              <div key={q.id}>
                <ServiceQuestion
                  question={q}
                  answer={state.serviceAnswers.find(
                    (a) => a.questionId === q.id,
                  )}
                  onAnswer={ignoreServiceAnswer}
                  headingLevel={2}
                  dcOrderingAnswer={dcOrderingAnswer}
                  dcSelfServeEnabled={dcSelfServeEnabled}
                  dcOrderingTemplate={dcOrderingTemplate}
                  dcTimelineFamilyOrders={config?.dc_timeline_family_orders}
                  dcTimelineAfterOrders={config?.dc_timeline_after_orders}
                  dcLovedOneName={lovedOneName}
                  dcSelectedOrdering={effectiveDcOrdering}
                  dcReadOnly={isDcOrderPlaced}
                  dcOrderedQuantity={orderedDcQuantity}
                  {...getInclusionProps(q.id)}
                />
                {shouldShowDeliveryAddressForm(q.id, state.serviceAnswers) && (
                  <DeliveryAddressSummary
                    address={state.fulfillment.shippingAddress}
                  />
                )}
              </div>
            ))}
          </div>
        ) : (
          currentQuestion && (
            <>
              <ServiceQuestion
                key={currentQuestion.id}
                question={currentQuestion}
                answer={currentAnswer}
                onAnswer={setServiceAnswerTracked}
                dcOrderingAnswer={dcOrderingAnswer}
                dcSelfServeEnabled={dcSelfServeEnabled}
                dcOrderingTemplate={dcOrderingTemplate}
                dcTimelineFamilyOrders={config?.dc_timeline_family_orders}
                dcTimelineAfterOrders={config?.dc_timeline_after_orders}
                dcLovedOneName={lovedOneName}
                dcSelectedOrdering={effectiveDcOrdering}
                dcReadOnly={isDcQuestionReadOnly}
                dcOrderedQuantity={orderedDcQuantity}
                {...inclusionProps}
              />
              {shouldShowDeliveryAddressForm(
                currentQuestion.id,
                state.serviceAnswers,
              ) && (
                <DeliveryAddressForm onValidityChange={setAddressFormValid} />
              )}
            </>
          )
        )}
      </div>

      {isReadOnly ? (
        <BackToReviewButton />
      ) : (
        <ArrangementNav
          onBack={handleBack}
          onContinue={handleContinue}
          continueLabel={
            isSavingDcOrdering ? "Saving…" : getContinueLabel(currentQuestion)
          }
          disableContinue={!canContinue}
          disableBack={isSavingDcOrdering}
          continueHint={continueHint}
        />
      )}
    </div>
  );
}
